import { useContext, useEffect, useState } from "react";
import { Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PeaceHand, Leaf } from "iconoir-react";
import { StreamContext } from "contexts/Stream";

type SessionWarningPopupProps = {
  isMobile?: boolean;
  expireTime: number;
  handleSessionContinue: (
    onSessionUpdateSuccess?: () => void,
    onSessionUpdateFail?: (ex: Error) => void,
  ) => void;
  handleSessionClose: () => void;
};

export default function SessionWarningPopup({
  isMobile,
  expireTime,
  handleSessionContinue,
  handleSessionClose,
}: SessionWarningPopupProps) {
  const { setStreamingState, setTurntableState } = useContext(StreamContext);
  const [countDown, setCountDown] = useState(expireTime);
  const [sessionUpdateFailed, setSessionUpdateFailed] = useState(false);
  const theme = useTheme();
  const isTab = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown((prev) => prev - 1);
    }, 1000);
    if (countDown === 1 || countDown < 1) {
      clearInterval(interval);
      handleSessionClose();
    }
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSessionUpdateSuccess = () => {
    console.log("session id updated successfull");
  };

  const handleSessionUpdateFail = () => {
    setSessionUpdateFailed(true);
    console.log("session id updation failed. Streaming will close soon");
  };

  return (
    <Container>
      {sessionUpdateFailed ? (
        <>
          <PeaceHand strokeWidth={1} width={50} height={50} />
          <Typography variant="h3" gutterBottom>
            Sorry about that!
          </Typography>
          <Typography variant="body1">
            Unfortunately, the session could not be extended. Streaming will close soon. Please
            reload the page and try again.
          </Typography>
        </>
      ) : (
        <>
          <Leaf strokeWidth={1} width={50} height={50} />
          <Typography color="primary" variant={isMobile || isTab ? "h5" : "h3"} gutterBottom>
            Session will end in {countDown}&nbsp;
            {countDown > 1 ? "seconds" : "second"}.
          </Typography>
          <Typography color="primary" variant="body1">
            If you no longer need the 3D scene, it will be closed for sustainability.
          </Typography>
          <CallToAction>
            <Button
              variant="outlined"
              sx={{ fontSize: { xs: 12, md: 16 } }}
              onClick={handleSessionClose}
            >
              Close session
            </Button>
            <Button
              variant="contained"
              sx={{ fontSize: { xs: 12, md: 16 } }}
              onClick={() =>
                handleSessionContinue(handleSessionUpdateSuccess, handleSessionUpdateFail)
              }
            >
              Continue exploring
            </Button>
          </CallToAction>
        </>
      )}
    </Container>
  );
}

const Container = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  gridColumnStart: 1,
  gridColumnEnd: 2,
  gridRowStart: 1,
  gridRowEnd: 2,
  placeSelf: "center",
  padding: theme.spacing(4),
  display: "flex",
  width: "90%",
  maxWidth: "600px",
  flexDirection: "column",
  backgroundColor: "rgba(0, 0, 0, 0.2)",
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  backdropFilter: "blur(5px)",
  [theme.breakpoints.down("md")]: {
    top: "40%",
  },
  [theme.breakpoints.down("sm")]: {
    top: "30%",
  },
}));

const CallToAction = styled("div")({
  marginTop: "2em",
  display: "flex",
  justifyContent: "space-between",
});
